import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';

// ── NOTIF.3 — bell reads the STORE (per-user, authenticated) via /api/v1/inbox. ──
// The computed placeholder GET /api/v1/notifications was retired (D-NOTIF-3-1).
// Store DTO (NOTIF.1/2b-i): the authenticated user's materialized notifications +
// this user's read/dismiss state. Deep-linking (resource_type/resource_id) is
// deferred to NOTIF.4 — the fields are carried here for a clean later add.
export interface Notification {
  id: string;
  category: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  body: string | null;
  resource_type: string | null;
  resource_id: string | null;
  source_event_type: string;
  created_at: string;
  was_read: boolean;
  read_at: string | null;
  dismissed_at: string | null;
  notes_count?: number; // COLLAB-NOTIF S2 (card 8): resource notes tally; absent/0 → no row indicator
}

const BELL_CAP = 10; // parity with the old computed feed's cap

// Bell list — the user's recent notifications from the store (capped).
export function useNotifications() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['inbox', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/inbox', { params: { perPage: BELL_CAP } });
      return data.data.data as Notification[];
    },
    enabled: !!tenantId,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

// Unread badge — indexed COUNT over the partial unread index (was_read=false AND
// dismissed_at IS NULL). BUG-NOTIF-BELL-UNREAD-UNDERCOUNT closes by construction.
export function useUnreadCount() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['inbox-unread', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/inbox/unread-count');
      return (data.data.count as number) ?? 0;
    },
    enabled: !!tenantId,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

// ── Optimistic inbox mutations (NOTIF.4 redesign P1) ─────────────────────────
// mark-read / dismiss apply INSTANTLY to the list + bell + unread caches, then
// roll back on error and reconcile on settle. Read-state figure (unread badge)
// tracks the optimistic change so it never lags the visible list.
type OptimisticCtx = {
  prevLists: [readonly unknown[], InboxListResult | undefined][];
  prevBells: [readonly unknown[], Notification[] | undefined][];
  prevUnread: [readonly unknown[], number | undefined][];
};

function useOptimisticInbox() {
  const qc = useQueryClient();

  async function begin(ids: Set<string>, op: 'read' | 'unread' | 'dismiss' | 'restore'): Promise<OptimisticCtx> {
    await Promise.all([
      qc.cancelQueries({ queryKey: ['inbox'] }),
      qc.cancelQueries({ queryKey: ['inbox-list'] }),
      qc.cancelQueries({ queryKey: ['inbox-unread'] }),
    ]);

    const prevLists = qc.getQueriesData<InboxListResult>({ queryKey: ['inbox-list'] });
    const prevBells = qc.getQueriesData<Notification[]>({ queryKey: ['inbox'] });
    const prevUnread = qc.getQueriesData<number>({ queryKey: ['inbox-unread'] });

    // Count targeted non-dismissed items by current read-state → the unread-badge delta (each id once).
    let unreadHit = 0; // currently unread (dismiss removes them → decrement)
    let readHit = 0;   // currently read (mark-unread flips them → increment)
    const counted = new Set<string>();
    const scan = (n: Notification) => {
      if (ids.has(n.id) && !counted.has(n.id) && !n.dismissed_at) {
        if (!n.was_read) unreadHit++; else readHit++;
        counted.add(n.id);
      }
    };
    prevLists.forEach(([, v]) => v?.data.forEach(scan));
    prevBells.forEach(([, v]) => v?.forEach(scan));

    const patch = (n: Notification): Notification | null => {
      if (!ids.has(n.id)) return n;
      if (op === 'dismiss' || op === 'restore') return null; // both leave the CURRENT list (inbox→archived / archived→inbox)
      if (op === 'unread') return n.was_read ? { ...n, was_read: false, read_at: null } : n;
      return n.was_read ? n : { ...n, was_read: true, read_at: new Date().toISOString() };
    };
    const keep = (n: Notification | null): n is Notification => n !== null;

    qc.setQueriesData<InboxListResult>({ queryKey: ['inbox-list'] }, (old) => {
      if (!old) return old;
      const data = old.data.map(patch).filter(keep);
      const removed = old.data.length - data.length;
      return { data, meta: { ...old.meta, total: Math.max(0, old.meta.total - removed) } };
    });
    qc.setQueriesData<Notification[]>({ queryKey: ['inbox'] }, (old) => (old ? old.map(patch).filter(keep) : old));
    // Unread badge: dismiss removes unread items (decrement); mark-unread flips read→unread (increment).
    // Restore leaves read-state untouched → reconciles on settle.
    if (op === 'dismiss' && unreadHit > 0) {
      qc.setQueriesData<number>({ queryKey: ['inbox-unread'] }, (old) => Math.max(0, (old ?? 0) - unreadHit));
    } else if (op === 'unread' && readHit > 0) {
      qc.setQueriesData<number>({ queryKey: ['inbox-unread'] }, (old) => (old ?? 0) + readHit);
    }

    return { prevLists, prevBells, prevUnread };
  }

  function rollback(ctx?: OptimisticCtx) {
    ctx?.prevLists.forEach(([k, v]) => qc.setQueryData(k, v));
    ctx?.prevBells.forEach(([k, v]) => qc.setQueryData(k, v));
    ctx?.prevUnread.forEach(([k, v]) => qc.setQueryData(k, v));
  }

  function settle() {
    qc.invalidateQueries({ queryKey: ['inbox'] });
    qc.invalidateQueries({ queryKey: ['inbox-list'] });
    qc.invalidateQueries({ queryKey: ['inbox-unread'] });
  }

  return { begin, rollback, settle };
}

// ── Notification center (NOTIF.4) — paginated + filtered list ────────────────
export interface InboxListParams {
  page?: number;
  perPage?: number;
  category?: string;
  priority?: string;
  state?: 'unread' | 'read' | 'all' | 'archived';
  q?: string;
}
export interface InboxListMeta { total: number; page: number; perPage: number; totalPages: number }
export interface InboxListResult { data: Notification[]; meta: InboxListMeta }

export function useInboxList(params: InboxListParams) {
  const { tenantId } = useAuthStore();
  // Drop empty/undefined params so the URL stays clean and matches the API defaults.
  const clean: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '' && v !== null) clean[k] = v;
  return useQuery({
    queryKey: ['inbox-list', tenantId, clean],
    queryFn: async () => {
      const { data } = await apiClient.get('/inbox', { params: clean });
      return data.data as InboxListResult;
    },
    enabled: !!tenantId,
    staleTime: 30_000,
    placeholderData: (prev) => prev, // keep prior page visible while the next loads
  });
}

// Bulk actions — ID-set model (the shipped /api/v1/inbox bulk contract, D-NOTIF-4-4).
// All four are optimistic (instant list/bell/unread update) with rollback-on-error.
export function useMarkReadBulk() {
  const opt = useOptimisticInbox();
  return useMutation({
    mutationFn: (ids: string[]) => apiClient.post('/inbox/read', { ids }),
    onMutate: (ids: string[]) => opt.begin(new Set(ids), 'read'),
    onError: (_e, _ids, ctx) => opt.rollback(ctx),
    onSettled: () => opt.settle(),
  });
}
export function useDismissBulk() {
  const opt = useOptimisticInbox();
  return useMutation({
    mutationFn: (ids: string[]) => apiClient.post('/inbox/dismiss', { ids }),
    onMutate: (ids: string[]) => opt.begin(new Set(ids), 'dismiss'),
    onError: (_e, _ids, ctx) => opt.rollback(ctx),
    onSettled: () => opt.settle(),
  });
}

export function useMarkNotificationRead() {
  const opt = useOptimisticInbox();
  return useMutation({
    mutationFn: (id: string) => apiClient.patch(`/inbox/${id}/read`),
    onMutate: (id: string) => opt.begin(new Set([id]), 'read'),
    onError: (_e, _id, ctx) => opt.rollback(ctx),
    onSettled: () => opt.settle(),
  });
}

// Mark-unread — additive mirror of mark-read. Optimistically flips read→unread + bumps the unread badge.
export function useMarkNotificationUnread() {
  const opt = useOptimisticInbox();
  return useMutation({
    mutationFn: (id: string) => apiClient.patch(`/inbox/${id}/unread`),
    onMutate: (id: string) => opt.begin(new Set([id]), 'unread'),
    onError: (_e, _id, ctx) => opt.rollback(ctx),
    onSettled: () => opt.settle(),
  });
}
export function useMarkUnreadBulk() {
  const opt = useOptimisticInbox();
  return useMutation({
    mutationFn: (ids: string[]) => apiClient.post('/inbox/unread', { ids }),
    onMutate: (ids: string[]) => opt.begin(new Set(ids), 'unread'),
    onError: (_e, _ids, ctx) => opt.rollback(ctx),
    onSettled: () => opt.settle(),
  });
}

export function useDismissNotification() {
  const opt = useOptimisticInbox();
  return useMutation({
    mutationFn: (id: string) => apiClient.patch(`/inbox/${id}/dismiss`),
    onMutate: (id: string) => opt.begin(new Set([id]), 'dismiss'),
    onError: (_e, _id, ctx) => opt.rollback(ctx),
    onSettled: () => opt.settle(),
  });
}

// Restore (un-archive) — P4. Optimistically leaves the archived list; read-state untouched (server truth).
export function useRestoreNotification() {
  const opt = useOptimisticInbox();
  return useMutation({
    mutationFn: (id: string) => apiClient.patch(`/inbox/${id}/restore`),
    onMutate: (id: string) => opt.begin(new Set([id]), 'restore'),
    onError: (_e, _id, ctx) => opt.rollback(ctx),
    onSettled: () => opt.settle(),
  });
}
export function useRestoreBulk() {
  const opt = useOptimisticInbox();
  return useMutation({
    mutationFn: (ids: string[]) => apiClient.post('/inbox/restore', { ids }),
    onMutate: (ids: string[]) => opt.begin(new Set(ids), 'restore'),
    onError: (_e, _ids, ctx) => opt.rollback(ctx),
    onSettled: () => opt.settle(),
  });
}

// ── COLLAB-NOTIF Slice 1 — resource status + actor + attention overlay ────────
// Server-authoritative normalization (the api owns the ratified display map — a
// tag promises the resource's state). The client only renders what it's given:
// `status` is the base display tag, `attention` is an ADORNMENT alongside it
// (never a replacement), `actorLine` is honest-empty (null → render nothing).
export interface ResourceStatusView {
  status: string;
  attention: boolean;
  actorLine: string | null;
}

// Batch-resolve for a set of notification ids (the server fans one query per
// family — no N+1). Keyed on the id set so opening a resolved notification reuses
// the cache. Absent entries = honest-empty (non-workflow notification, or the
// resource isn't visible) → the caller renders no status.
export function useResourceStatuses(ids: string[]) {
  const { tenantId } = useAuthStore();
  const key = [...ids].sort();
  return useQuery({
    queryKey: ['inbox-status', tenantId, key],
    queryFn: async () => {
      const { data } = await apiClient.post('/inbox/resource-status', { ids });
      return data.data as Record<string, ResourceStatusView>;
    },
    enabled: !!tenantId && ids.length > 0,
    staleTime: 60_000,
  });
}
